/**
 * cells (#2646) — the MIGRATED Preisspiegel read path.
 *
 * The page used to walk raw ``price_observation`` derived facts: one fact per
 * priced invoice line, paged 10 × 1000, and truncating on any tenant past
 * ~10,000 lines (WF passed 33,000 — erdbeeren alone is ~19–20k). It now reads
 * ``GET /twin/preisspiegel/cells``: ``(day × series)`` cells that UDB has
 * already collapsed VOLUME-WEIGHTED at the source, for the whole catalog, in a
 * single call. The payload is O(days × series) instead of O(invoice lines), so
 * the ceiling moved from "how much did this farm sell" to "how many days did it
 * sell on" — a number that cannot run away.
 *
 * This module is the reshape only. Like ``ownPriceAdapter`` before it, it does
 * NOT re-derive the document-line → catalog-item crosswalk, which is
 * backend-owned by explicit contract (#2394); it reads keys the server grouped
 * by and hands back the same ``PriceSeries`` shape every chart, tile and panel
 * already consumes, so nothing downstream had to change to receive it.
 *
 * ── What a cell is, and what it costs ────────────────────────────────────────
 *
 * One cell = one day of one counterparty series. Its ``volume_weighted_mean``
 * is the price the farmer actually realised that day (Σ money ÷ Σ kilos), which
 * is the #2573 fix finally reaching a user surface — the old client-side p50
 * median over per-line values carried a pack-size grain artefact (the pinned
 * REWE 8,80 / 5,20 case) because a 2 kg tray and a 500 g punnet counted the
 * same.
 *
 * The honest cost: a cell is not a line, so the panel's rows are now per
 * (counterparty, quality, channel) per day rather than per invoice line.
 * ``n`` is carried through as {@link ObservedPoint.lineCount} so "N
 * Beobachtungen" and "× N Positionen" keep counting LINES; ``total_amount``
 * is carried as the point's ``provenance.amount`` so the traded weight
 * survives (see below). What genuinely does not survive is per-line
 * DOCUMENT provenance: a cell knows how many lines it folded and what they were
 * worth, not which document each came from.
 *
 * ── Why ``provenance.amount = total_amount`` is exact, not a fudge ───────────
 *
 * ``aggregate.catalogUnitKg`` is ``amount / value``. At line grain that is the
 * line's money over the €/kg it resolved to. At cell grain ``total_amount`` is
 * Σ of those same line amounts and ``volume_weighted_mean`` is their
 * kg-weighted price, so ``total_amount / volume_weighted_mean`` is Σ kilos —
 * the same identity evaluated one level up. The field means the same thing at
 * both grains ("the money on this row"), which is why the day panel needed no
 * change to keep weighing rows correctly.
 *
 * That only holds because the server now weights by ``provenance.amount /
 * value`` (#2663). Under the previous ``provenance.quantity /
 * provenance.unit_factor`` basis every one of ``total_quantity``,
 * ``total_amount`` and ``volume_weighted_mean`` was computed off a PACKAGE
 * COUNT on kolli/steige/schale lines, and no arithmetic over them could have
 * recovered a real weight — in particular ``total_amount /
 * volume_weighted_mean`` reduces to ``total_quantity`` identically, so it was
 * never an escape hatch.
 */
import {
  CHANNELS,
  PRICE_SIDES,
  PRICE_UNITS,
  QUALITIES,
  allItems,
  type Channel,
  type PriceSide,
  type PriceUnit,
  type Quality,
} from './catalog';
import type { PriceCell } from '../../../services/twinService';
import type { ObservedPoint, OutOfBandMark, PriceSeries } from './types';

export type { PriceCell };

const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;

const str = (v: unknown): string | undefined => (typeof v === 'string' && v !== '' ? v : undefined);

const num = (v: unknown): number | undefined => {
  const n = typeof v === 'number' ? v : typeof v === 'string' ? Number(v) : NaN;
  return Number.isFinite(n) ? n : undefined;
};

const side = (v: unknown): PriceSide | undefined =>
  typeof v === 'string' && (PRICE_SIDES as readonly string[]).includes(v) ? (v as PriceSide) : undefined;

const quality = (v: unknown): Quality | undefined =>
  typeof v === 'string' && (QUALITIES as readonly string[]).includes(v) ? (v as Quality) : undefined;

const channel = (v: unknown): Channel | undefined =>
  typeof v === 'string' && (CHANNELS as readonly string[]).includes(v) ? (v as Channel) : undefined;

/**
 * The unit the cell's facts were PUBLISHED under (#2944), where the server
 * grouped on it.
 *
 * The CATALOG-WIDE read does not: ``CATALOG_CELL_GROUP_BY`` is at UDB's six-key
 * ceiling and ``unit`` is the key that was traded away for ``catalog_item_id``,
 * so on the live page this is absent and the unit reaches the tile from
 * ``/index`` instead. The per-item ``CELL_GROUP_BY`` does keep it. Reading it
 * whenever it is there costs nothing and keeps the two paths from disagreeing
 * about one payload — absence stays "ask elsewhere", never a guessed default.
 */
const priceUnit = (v: unknown): PriceUnit | undefined =>
  typeof v === 'string' && (PRICE_UNITS as readonly string[]).includes(v) ? (v as PriceUnit) : undefined;

interface ParsedCell {
  seriesId: string;
  itemId: string;
  side: PriceSide;
  counterparty: string;
  quality?: Quality;
  channel?: Channel;
  unit?: PriceUnit;
  date: string;
  value: number;
  /** Σ catalog units (kg) this cell accounts for, or null when it cannot state one. */
  kg: number | null;
  /** Σ € this cell accounts for, or null when it cannot state one. */
  amount: number | null;
  /** Rows folded into this cell. */
  lineCount: number;
  /**
   * Lowest / highest LINE price inside this cell (#2671), or null where the
   * cell states none. Unlike the totals these are NOT withheld on a partly
   * weighted cell: an extremum is an observed price, and ``min``/``max`` range
   * over every row that carried a VALUE (``value_missing`` is counted
   * separately), so they never depend on a weight the way a Σ does.
   */
  min: number | null;
  max: number | null;
  /**
   * The cell IS volume-weighted, but only over some of its rows
   * (``weighted && weight_missing > 0``) — v34.6.0.
   *
   * Carried alongside the withheld totals rather than inferred from them: once
   * ``amount``/``kg`` are null, "weighted over a subset" and "never weighted at
   * all" are the same two nulls, and only the first may be described as a
   * weighted price.
   */
  weightPartial: boolean;
  /** The server's plausibility verdict for this cell (HOF-19), or undefined. */
  outOfBand?: OutOfBandMark;
}

/**
 * The server's ``out_of_band`` block in the app's own casing, or undefined.
 *
 * Coerced field by field rather than spread, for the reason every parser in this
 * file is written that way: the payload is untyped JSON at runtime, and a mark
 * with a missing edge would render "außerhalb von undefined–undefined €/kg" on a
 * screen whose entire argument is that it says what it did. Any field missing or
 * unusable yields NO mark at all — an absent mark is a safe answer ("not
 * judged"), a half-populated one is a claim nobody can check.
 */
function parseOutOfBand(raw: PriceCell['out_of_band']): OutOfBandMark | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const crop = str(raw.crop);
  const unit = str(raw.unit);
  const publishedUnit = str(raw.published_unit);
  const value = num(raw.value);
  const bandMin = num(raw.band_min);
  const bandMax = num(raw.band_max);
  const direction = raw.direction === 'high' || raw.direction === 'low' ? raw.direction : undefined;
  if (!crop || !unit || !publishedUnit || !direction) return undefined;
  if (value === undefined || bandMin === undefined || bandMax === undefined) return undefined;
  return { crop, unit, value, publishedUnit, bandMin, bandMax, direction };
}

/**
 * Parse one cell, or null when a required field is missing/unusable.
 *
 * ``volume_weighted_mean`` is the value; ``mean`` is the documented fallback for
 * a cell UDB could not weight (it reports ``weighted: false`` and sets
 * ``volume_weighted_mean`` to the unweighted mean itself, so this only fires on
 * a payload that omitted the key entirely).
 *
 * **The #2777 estimate slice parses here unchanged, and that is deliberate**:
 * both populations arrive in one item shape, so one parser reads both and the
 * slice is stamped by the caller. A curated cell is simply an unweighted one —
 * a reference price never had a traded weight — so the existing
 * ``fullyWeighted`` rule withholds its ``amount``/``kg`` for the right reason,
 * and the day panel cannot end up crediting the book with tonnage.
 */
function parseCell(cell: PriceCell): ParsedCell | null {
  if (!cell || typeof cell !== 'object') return null;
  const keys = cell.keys;
  if (!keys || typeof keys !== 'object') return null;

  const seriesId = str(keys.series_id);
  const itemId = str(keys.catalog_item_id);
  const s = side(keys.side);
  const date = str(cell.bucket);
  const value = num(cell.volume_weighted_mean) ?? num(cell.mean);
  if (!seriesId || !itemId || !s || !date || !ISO_DAY.test(date) || value === undefined) return null;

  const counterparty = str(keys.counterparty) ?? '—';

  // A money/weight total is the WHOLE cell's or none.
  //
  // Two ways a cell can fail to have one. ``weighted: false`` — no line carried
  // a usable weight, so there is nothing to total. And ``weight_missing > 0`` —
  // SOME lines did: UDB still reports ``weighted: true``, but ``total_amount``
  // then covers only that subset while ``n`` counts them all. Taking it anyway
  // would state the tonnage of three lines against a row that says five, which
  // is precisely what ``aggregate.collapseSources`` refuses to do at line grain
  // ("a partial sum would misstate the total, so one amount-less member nulls
  // the whole row"). The same contract has to hold one level up, or the
  // migration would quietly weaken it.
  //
  // The PRICE stays trustworthy either way — it is weighted across whichever
  // lines had a weight — so only the total is withheld, never the point.
  const weightMissing = num(cell.weight_missing) ?? 0;
  const fullyWeighted = Boolean(cell.weighted) && weightMissing === 0;
  const amount = fullyWeighted ? (num(cell.total_amount) ?? null) : null;
  const kg = fullyWeighted ? (num(cell.total_quantity) ?? null) : null;
  // Kept even though the totals are withheld — see ``ParsedCell.weightPartial``.
  const weightPartial = Boolean(cell.weighted) && weightMissing > 0;

  return {
    seriesId,
    itemId,
    side: s,
    counterparty,
    quality: quality(keys.quality),
    channel: channel(keys.channel),
    unit: priceUnit(keys.unit),
    date,
    value,
    kg,
    amount,
    weightPartial,
    lineCount: Math.max(num(cell.n) ?? 1, 1),
    // ``num`` rejects NaN/Infinity, so a garbage extremum reads as "not stated"
    // rather than poisoning a Math.min/max downstream.
    min: num(cell.min) ?? null,
    max: num(cell.max) ?? null,
    outOfBand: parseOutOfBand(cell.out_of_band),
  };
}

/**
 * Fold every cell of one ``(series, day)`` into a single point.
 *
 * Normally there is exactly one, but the read groups by the RAW ``counterparty``
 * as well as ``series_id``, and ``series_id`` carries only the counterparty
 * SLUG — so two spellings that slugify alike ("REWE" / "Rewe Group") arrive as
 * two cells on one series-day. Appending both would put two observations where
 * the day had one, and every median/percentile downstream would then be taken
 * over sub-cells rather than over the recomposed day price.
 *
 * The recomposition mirrors ``build_series_from_cells`` on the server (which
 * already folds this case for the per-item route) so the two read paths cannot
 * disagree about the same data: weighted sub-cells recombine as Σ€ ÷ Σkg, and
 * if ANY sub-cell is weighted the day is theirs — an unweighted one has no
 * volume with which to claim a share of the day, and letting it vote would drag
 * the price by a quantity nobody can state. Only when NO sub-cell is weighted
 * does it fall back to the row-count-weighted mean of their values.
 *
 * A day that is only PARTLY accounted for therefore reports no total at all,
 * for the same reason a partly-weighted cell does.
 *
 * ── Extrema fold by MIN/MAX, never weighted (#2671) ──────────────────────────
 *
 * The PRICE recomposes as Σ€ ÷ Σkg because it answers "what did the day
 * realise". The RANGE answers a different question — "what did any single line
 * fetch" — and a volume-weighted blend of two maxima is neither observation: a
 * lone line at 12,00 happened however little it weighed, and weighting it away
 * would report a high the farm never traded at. So ``Math.min``/``Math.max``
 * across the sub-cells, flat.
 *
 * Two rules make the fold TOTAL — it always yields a coherent range that
 * contains the day's own ``value``, whatever the payload says:
 *
 *   1. **A sub-cell's own price stands in for extrema it does not state.**
 *      Skipping such a sub-cell is the trap: the price is dragged towards it
 *      while the range still reflects only the others, which can put the low
 *      ABOVE the day's price — an inverted wick. A cell's price always lies
 *      inside its own true range, so standing it in is exact when the cell
 *      folded one line and conservative (never too wide) otherwise. It also
 *      neutralises an incoherent ``min > max`` pair instead of propagating it.
 *   2. **Every sub-cell contributes, weighted or not.** An unweighted sub-cell
 *      is excluded from the price — it has no volume with which to claim a
 *      share of the day — but its lines were still observed, so the range has
 *      to reach them.
 *
 * When NO sub-cell states an extremum the pair is omitted entirely rather than
 * collapsed onto ``value``, so consumers' ``obsMin ?? value`` fallback fires and
 * "no range stated" stays distinguishable from "a real range of zero width".
 */
/**
 * The verdict for a FOLD of sub-cells — all-or-nothing, and rescaled.
 *
 * ── Why not "any marked sub-cell marks the fold" ─────────────────────────────
 *
 * That was the first cut, and codex was right to red it. Two sub-cells arise
 * when one series-day carries two counterparty SPELLINGS ("REWE" / "Rewe
 * Group"), and their prices fold volume-weighted. So a 1 kg sale at 112,59 €/kg
 * beside a 1.000 kg sale at 8,00 folds to about 8,10 — comfortably in band — and
 * marking that fold does two wrong things at once. It **excludes 1.000 kg of
 * perfectly good volume from the day's average**, which is the over-correction
 * this whole ticket is written against; and it **shows a verdict computed for a
 * number that is not the one on screen**, on a chart whose entire argument is
 * that it says what it did.
 *
 * ── Why all-or-nothing is not merely safer, but SOUND ────────────────────────
 *
 * If every sub-cell breaches the same edge, the fold does too, and provably: a
 * weighted mean with strictly positive weights lies between its inputs, so a
 * mean of values all above ``high`` is above ``high``. That is the one case
 * where the client can assert a verdict it did not compute. Sub-cells breaching
 * OPPOSITE edges are excluded for the mirror reason — their mean can land
 * anywhere, in band included.
 *
 * ── What is lost, and why it is not lost ─────────────────────────────────────
 *
 * A mixed fold now carries no mark on the chart point. The artefact is NOT
 * hidden: the server still marks that cell, the point is still plotted at its
 * own value, and the drill-down lists the offending line with its own verdict
 * (``/preisspiegel/lines``, AC 11). The badge is one of four places a reader can
 * meet this, and it is the only one where showing it would cost a thousand
 * kilos of somebody's real trade.
 *
 * ── The rescale ──────────────────────────────────────────────────────────────
 *
 * ``mark.value`` is the sub-cell's price in €/kg, and ``value`` is the fold's in
 * the published unit; the ratio between a sub-cell's two numbers IS the unit
 * conversion, and it is the same for every sub-cell of one series. So the fold's
 * own €/kg is ``foldedValue × (mark.value / firstMarkValue)``, exactly — and the
 * tooltip then quotes the number the reader is looking at rather than one of its
 * inputs. Guarded against a zero divisor, which ``bands.out_of_band`` cannot
 * produce (it declines a non-positive value) but which nothing here enforces.
 */
function foldedVerdict({
  everyPartMarked,
  firstMark,
  firstMarkValue,
  foldedValue,
}: {
  everyPartMarked: boolean;
  firstMark: OutOfBandMark | undefined;
  firstMarkValue: number;
  foldedValue: number;
}): OutOfBandMark | undefined {
  if (!everyPartMarked || firstMark === undefined) return undefined;
  if (!(firstMarkValue > 0) || !Number.isFinite(foldedValue)) return firstMark;
  return { ...firstMark, value: foldedValue * (firstMark.value / firstMarkValue) };
}

function foldToPoint(parts: ParsedCell[]): ObservedPoint {
  let wNum = 0; // Σ (value × kg) over weighted sub-cells
  let wDen = 0; // Σ kg
  let uNum = 0; // Σ (value × n) over unweighted sub-cells
  let uDen = 0; // Σ n
  let amount = 0;
  let lineCount = 0;
  let everyPartWeighted = true;
  // v34.6.0 — true once ANY sub-cell was itself weighted over only part of its
  // rows. Separate from `everyPartWeighted`, which is about whether this fold
  // may state a total; this is about whether the PRICE covers everything.
  let anyPartWeightPartial = false;
  let obsMin = Number.POSITIVE_INFINITY;
  let obsMax = Number.NEGATIVE_INFINITY;
  let anyExtremum = false;
  // HOF-19 — the fold's verdict, and it is ALL-OR-NOTHING. See
  // ``foldedVerdict`` below for why the obvious "any marked sub-cell marks the
  // fold" is wrong on both counts.
  let everyPartMarked = true;
  let firstMark: OutOfBandMark | undefined;
  let firstMarkValue = 0;

  for (const part of parts) {
    lineCount += part.lineCount;
    if (part.weightPartial) anyPartWeightPartial = true;
    if (part.outOfBand === undefined) {
      everyPartMarked = false;
    } else if (firstMark === undefined) {
      firstMark = part.outOfBand;
      firstMarkValue = part.value;
    } else if (part.outOfBand.direction !== firstMark.direction) {
      // Two sub-cells breaching OPPOSITE edges. Their weighted mean can land
      // anywhere, including comfortably in band, so neither verdict is true of
      // the fold. Treated exactly like a mixed marked/unmarked fold.
      everyPartMarked = false;
    }
    if (part.min !== null) {
      obsMin = Math.min(obsMin, part.min);
      anyExtremum = true;
    }
    if (part.max !== null) {
      obsMax = Math.max(obsMax, part.max);
      anyExtremum = true;
    }
    // Rule 1 + 2 above: the sub-cell's own price always votes on the range.
    obsMin = Math.min(obsMin, part.value);
    obsMax = Math.max(obsMax, part.value);
    if (part.kg !== null && part.kg > 0 && part.amount !== null) {
      wNum += part.value * part.kg;
      wDen += part.kg;
      amount += part.amount;
    } else {
      everyPartWeighted = false;
      uNum += part.value * part.lineCount;
      uDen += part.lineCount;
    }
  }

  const value = wDen > 0 ? wNum / wDen : uDen > 0 ? uNum / uDen : parts[0].value;
  const point: ObservedPoint = { date: parts[0].date, value };
  if (everyPartWeighted && wDen > 0) point.provenance = { amount };
  point.lineCount = lineCount;
  // Two independent ways this day's price covers less than the day did, and
  // either is enough: a sub-cell UDB already weighted over only part of its
  // rows, or a mix of weighted and unweighted sub-cells here — where `value`
  // above takes the weighted branch and the unweighted sub-cells drop out.
  if (anyPartWeightPartial || (wDen > 0 && uDen > 0)) point.weightPartial = true;
  const outOfBand = foldedVerdict({ everyPartMarked, firstMark, firstMarkValue, foldedValue: value });
  if (outOfBand !== undefined) point.outOfBand = outOfBand;
  if (anyExtremum) {
    // `value` is a mean over the sub-cell values with strictly positive
    // weights, so it already lies inside [obsMin, obsMax]. Re-folding it in
    // costs nothing and makes the documented invariant hold EXACTLY rather than
    // up to a rounding ulp — a wick must never render inverted, however finely.
    point.obsMin = Math.min(obsMin, value);
    point.obsMax = Math.max(obsMax, value);
  }
  return point;
}

/**
 * Which population a batch of cells came from (#2777).
 *
 * ``/cells`` returns two arrays in the SAME item shape — booked trades in
 * ``cells``, the curated price book in ``estimate_cells`` — and the aggregate
 * response carries no provenance block, so which array a cell arrived in is the
 * only thing that distinguishes them. Threading it onto the series here is what
 * keeps that knowable downstream; drop it and a 38 €/t reference price becomes
 * indistinguishable from a price a buyer paid, which is #2695 exactly.
 */
export interface CellSliceOptions {
  /** ``true`` for the curated price book. Omitted/false for booked observations. */
  estimate?: boolean;
}

/** Group ``(day × series)`` cells into own-layer {@link PriceSeries}. */
export function cellsToOwnSeries(cells: PriceCell[], opts: CellSliceOptions = {}): PriceSeries[] {
  const bySeries = new Map<string, PriceSeries>();
  const bySeriesDay = new Map<string, Map<string, ParsedCell[]>>();

  for (const cell of cells) {
    const parsed = parseCell(cell);
    if (!parsed) continue;
    if (!bySeries.has(parsed.seriesId)) {
      bySeries.set(parsed.seriesId, {
        seriesId: parsed.seriesId,
        itemId: parsed.itemId,
        side: parsed.side,
        counterparty: parsed.counterparty,
        ...(parsed.quality ? { quality: parsed.quality } : {}),
        ...(parsed.channel ? { channel: parsed.channel } : {}),
        ...(parsed.unit ? { unit: parsed.unit } : {}),
        points: [],
        layer: 'own',
        // Stamped only on the estimate slice, so a booked series stays byte-
        // identical to its pre-#2777 shape and "no flag" keeps meaning "booked".
        ...(opts.estimate ? { estimate: true as const } : {}),
      });
      bySeriesDay.set(parsed.seriesId, new Map());
    }
    const days = bySeriesDay.get(parsed.seriesId) as Map<string, ParsedCell[]>;
    const bucket = days.get(parsed.date);
    if (bucket) bucket.push(parsed);
    else days.set(parsed.date, [parsed]);
  }

  for (const [seriesId, series] of bySeries) {
    const days = bySeriesDay.get(seriesId) as Map<string, ParsedCell[]>;
    series.points = [...days.values()].map(foldToPoint).sort((a, b) => a.date.localeCompare(b.date));
  }
  return [...bySeries.values()];
}

/**
 * {@link cellsToOwnSeries} grouped one level up, keyed by catalog item id.
 *
 * Both #2777 slices land in ONE list per item, each series carrying its own
 * ``estimate`` stamp. One list because they belong to the same item and the same
 * tile; stamped because everything that computes a statistic cuts on
 * ``!s.estimate`` first. Two parallel maps would have made that cut opt-in — and
 * a consumer that forgot would silently average a reference price into a price
 * a buyer paid, which is the failure this whole issue is about.
 *
 * An item present ONLY in the estimate slice gets a key here, which is the other
 * half of the #2694 tile fix: the index re-admits it to the page, and this is
 * what stops it arriving at that tile with no series and rendering "Keine Daten".
 */
export function buildOwnSeriesByItemFromCells(
  cells: PriceCell[],
  estimateCells: PriceCell[] = [],
): Record<string, PriceSeries[]> {
  const byItem: Record<string, PriceSeries[]> = {};
  for (const series of cellsToOwnSeries(cells)) {
    (byItem[series.itemId] ??= []).push(series);
  }
  for (const series of cellsToOwnSeries(estimateCells, { estimate: true })) {
    (byItem[series.itemId] ??= []).push(series);
  }
  return byItem;
}

/**
 * Mirror-vs-server drift detector (#2420), the cell-path twin of
 * ``ownPriceAdapter.unmatchedItemIds``: catalog item ids the server returned
 * that match no item in the FE catalog mirror. Such a series is keyed under an
 * id nothing ever reads, so it would vanish from every view in silence.
 */
export function unmatchedCellItemIds(cells: PriceCell[]): string[] {
  const known = new Set(allItems().map((i) => i.id));
  return Object.keys(buildOwnSeriesByItemFromCells(cells)).filter((id) => !known.has(id));
}
